/**
 * GT-TENANCY-TRIPS-FILTER-VEHICLE-SCOPE-HOTFIX-01 — tenant/user-scoped
 * client persistence + cross-identity cleanup.
 *
 * Why this exists
 * ===============
 * Several client surfaces persisted filter / preference state under
 * tenant- and user-NEUTRAL `localStorage` keys that were never cleared
 * on logout (Trips vehicle selection `gt.trips.filter.vehicleIds.v1`,
 * `useSavedFilters` `gt:wave-j:filters:<scope>`, `useColumnPreferences`
 * `gt:wave-j:columns:<scope>`, Reports `gt-reports-columns-v1-*`). On a
 * SHARED browser the previous (tenant, user)'s identifiers were read by
 * the next session — the Phase 0 finding (`support_smdtt` then
 * `Owner/Geletec` both saw the same 93 stale vehicle chips). The backend
 * is correctly tenant-scoped (RLS), so no foreign business data leaked,
 * but cross-tenant *identifiers* were exposed in the UI (P1).
 *
 * The structural fix has three layers:
 *   1. Identity-scoped keys (this module's {@link scopedKey}) — user B
 *      can never READ user A's key because the suffix differs. This
 *      preserves same-user recall across logout/login (Owner finding 7)
 *      while closing the cross-(tenant,user) leak.
 *   2. Cleanup on identity change ({@link reconcileClientIdentity}) and
 *      on logout ({@link clearClientStateForLogout}) — purges ONLY the
 *      leak-prone LEGACY UNSCOPED keys, and drops the in-memory React
 *      Query cache on a genuine identity switch. Other identities'
 *      SCOPED keys are RETAINED (the suffix already makes them
 *      unreadable by the active identity), so a user's own preferences
 *      survive even after a different user used the same browser.
 *   3. Roster validation at the consuming surface (Trips) — see
 *      `trips/tripsFilterPersistence.ts#filterToRoster`.
 *
 * This module is framework-free (no React) so it is trivially testable
 * and can be called from both hooks and the plain `logout()` helper.
 */
import { EXTERNAL_PREFERENCE_KEYS, PREFERENCE_KEY_BASES } from "../preferences/keyBases.js";

export interface ClientIdentity {
  readonly tenantId: string;
  readonly userSub: string;
}

/** Minimal shape of `SessionInfo` this module reads. */
interface SessionLike {
  readonly tenantId?: string | null;
  readonly identity?: { readonly sub?: string | null } | null;
}

/**
 * Derive the persistence identity from a session. Returns `null` when
 * either the tenant id or the Keycloak subject is missing so callers
 * fall back to the pre-identity (unscoped) behaviour rather than writing
 * a half-formed key.
 */
export function identityFromSession(
  session: SessionLike | null | undefined,
): ClientIdentity | null {
  const tenantId = session?.tenantId ?? null;
  const userSub = session?.identity?.sub ?? null;
  if (!tenantId || !userSub) return null;
  return { tenantId, userSub };
}

/**
 * Suffix appended to a base persistence key to bind it to one
 * (tenant, user). The `::` sentinel cannot appear in a tenant UUID or a
 * Keycloak `sub`, so it is an unambiguous separator.
 *
 * Returns the EMPTY string when no identity is available, so the
 * pre-identity key shape is unchanged for framework-free hook unit tests
 * and any pre-auth caller. Production surfaces always render under an
 * authenticated session, so a real suffix is always applied there.
 */
export function identitySuffix(identity: ClientIdentity | null): string {
  if (!identity) return "";
  return `::${identity.tenantId}::${identity.userSub}`;
}

/** Append the identity suffix to a base key. */
export function scopedKey(baseKey: string, identity: ClientIdentity | null): string {
  return `${baseKey}${identitySuffix(identity)}`;
}

/**
 * `localStorage` base keys / prefixes whose entries are tenant-sensitive
 * and therefore must be partitioned by identity. The cleanup sweep uses
 * this list to recognise OUR keys (and only ours — PWA-hint,
 * feedback-draft, post-login-grace etc. are deliberately excluded).
 *
 * GT-USER-PREFERENCES-STATE-PERSISTENCE-01 — DERIVED FROM THE PREFERENCE
 * REGISTRY (single source of truth) so the cleanup sweep can never drift
 * from the declared identity-scoped surfaces. The set is unchanged from
 * #827: the managed preference bases (`gt.trips.filter.vehicleIds.v1`,
 * `gt:wave-j:filters:`, `gt:wave-j:columns:`) plus the legacy cleanup-only
 * base `gt-reports-columns-v1-`. See
 * `preferences/registry.ts#PREFERENCE_KEY_BASES`.
 */
export const TENANT_SCOPED_KEY_BASES: readonly string[] = PREFERENCE_KEY_BASES;

/** Records the identity that last owned this browser's persisted state. */
export const ACTIVE_IDENTITY_MARKER_KEY = "gt.client.activeIdentity.v1";

function getLocalStorage(): Storage | null {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function isTenantScopedKey(key: string): boolean {
  return TENANT_SCOPED_KEY_BASES.some((base) => key === base || key.startsWith(base));
}

/**
 * Browser-GLOBAL preference keys (locale, theme) that the cleanup sweep must
 * NEVER remove — they are read pre-login, carry no tenant/user data, and
 * must survive logout / identity change. Derived from the registry's
 * external preferences (single source of truth). This is an EXPLICIT guard
 * so even a future tenant-scoped base that prefix-matched one of these keys
 * could not cause it to be purged (the GT-…-DL-097 no-purge guarantee).
 */
function isProtectedGlobalKey(key: string): boolean {
  return EXTERNAL_PREFERENCE_KEYS.includes(key);
}

function snapshotKeys(storage: Storage): string[] {
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (key !== null) keys.push(key);
  }
  return keys;
}

function removeKeys(storage: Storage, keys: readonly string[]): void {
  for (const key of keys) {
    try {
      storage.removeItem(key);
    } catch {
      // best-effort — quota/availability errors are non-fatal.
    }
  }
}

/**
 * Remove ONLY the leak-prone LEGACY UNSCOPED tenant keys: entries that
 * match a tenant-scoped base but carry NO identity suffix (`::`). These
 * are the pre-hotfix unscoped keys (`gt.trips.filter.vehicleIds.v1`,
 * `gt:wave-j:filters:<scope>`, …) and the legacy cleanup-only bases
 * (`gt-reports-columns-v1-*`, `gt.sprint26.feedback.lastBody`) that a
 * shared browser would otherwise leak to the next session.
 *
 * Identity-SCOPED keys (carrying a `::<tenant>::<sub>` suffix) are
 * RETAINED regardless of which identity owns them. The suffix already
 * makes them unreadable by any other identity ({@link scopedKey}), so
 * deleting a different user's scoped preferences when the current user
 * logs in is BOTH unnecessary (no leak is possible) AND harmful — it
 * destroys that user's own same-browser recall. Retaining foreign scoped
 * keys is the fix for the GT-USER-PREFERENCES-STATE-PERSISTENCE-01
 * retention regression (User A → User B → User A lost A's preferences).
 *
 * Browser-GLOBAL external keys (locale, theme — {@link isProtectedGlobalKey})
 * are ALSO never removed: they are read pre-login and must survive logout /
 * identity change (the DL-097 theme/locale no-purge guarantee).
 *
 * Returns the number of keys removed (for tests / diagnostics).
 */
export function purgeLegacyUnscopedState(storage: Storage | null = getLocalStorage()): number {
  if (!storage) return 0;
  const toRemove = snapshotKeys(storage).filter(
    (key) => isTenantScopedKey(key) && !key.includes("::") && !isProtectedGlobalKey(key),
  );
  removeKeys(storage, toRemove);
  return toRemove.length;
}

export interface CacheClearable {
  clear(): void;
}

/**
 * Reconcile this browser's persisted client state with the
 * just-authenticated identity. Call once per resolved session, BEFORE
 * rendering authenticated children (from `SessionProvider`), so the
 * cache clear below cannot wipe a query a child has already mounted
 * (the post-login "header chips missing until refresh" regression).
 *
 *   - Purges the leak-prone LEGACY UNSCOPED keys (see
 *     {@link purgeLegacyUnscopedState}). Other identities' SCOPED keys
 *     are RETAINED — they are isolated by their suffix and belong to
 *     their owner, so a different user logging in must NOT delete them.
 *   - Drops the in-memory React Query cache when the identity DIFFERS
 *     from the previously-recorded one, so a re-auth without a full
 *     reload cannot surface the previous user's cached data. This
 *     touches only in-memory query state, never the persisted prefs.
 *
 * No-op when no usable identity is supplied.
 */
export function reconcileClientIdentity(
  identity: ClientIdentity | null,
  queryCache?: CacheClearable,
): void {
  const suffix = identitySuffix(identity);
  if (suffix === "") return;
  const storage = getLocalStorage();
  purgeLegacyUnscopedState(storage);

  const previous = storage?.getItem(ACTIVE_IDENTITY_MARKER_KEY) ?? null;
  if (previous !== null && previous !== suffix) {
    queryCache?.clear();
  }
  try {
    storage?.setItem(ACTIVE_IDENTITY_MARKER_KEY, suffix);
  } catch {
    // best-effort.
  }
}

/**
 * Logout cleanup. Removes the leak-prone pre-hotfix UNSCOPED keys (and
 * optionally drops the query cache if a client is supplied). Every
 * identity's SCOPED entries are kept — the active user's so a same-user
 * re-login restores their selection (Owner finding 7), and any other
 * user's because they are isolated by their suffix and must survive a
 * different user using this browser. The Keycloak end-session redirect
 * reload wipes the in-memory query cache regardless, so the `queryCache`
 * argument is an optional belt-and-suspenders for callers that have one.
 */
export function clearClientStateForLogout(queryCache?: CacheClearable): void {
  queryCache?.clear();
  // Identical hygiene to reconcileClientIdentity so both paths agree:
  // only UNSCOPED (legacy) tenant keys are removed; every `::`-suffixed
  // scoped key is retained for its owning identity.
  purgeLegacyUnscopedState(getLocalStorage());
}
